import _ from 'lodash';
import {
    AuthApi,
    Configuration,
    CoreFetch,
    FetchParams,
    Middleware,
    OAuthApi,
    OAuthStandardTokenResponse,
    RequestContext,
    ResponseContext,
    setCoreConfiguration,
} from '../../api';
import { TableStorage } from '../../ui/data-table';
import { createContractMiddleware } from '../contract/contract.middleware';
import { AuthStorage } from './auth.storage';
import { DemoModeBlockedError, isDemoModeActive, isDemoRequestAllowed } from './demo-mode.utils';
import { IErrorContextProps } from './error.provider';

/* tslint:disable:no-console */

const URL_BASE = process.env.REACT_APP_AUTH_URL;
const API_BASE = process.env.REACT_APP_API_URL;

export type IAuthFetchOptions = {
    skipErrorHandler?: boolean;
};

export interface IFetchController {
    group: string;
    opts?: IAuthFetchOptions;
    map: Map<string, AbortController[]>;
}

export type ClientID = 'PROVIDER_DASHBOARD' | 'SUPPORT_TOOL' | 'LOGIN' | 'PATIENT';

export async function exchangeV2Token(code: string, authStorage: AuthStorage): Promise<void> {

    const authV2TokenExchangeResponse = await CoreFetch<OAuthApi>(OAuthApi).authV2TokenExchange({
        grantType: 'code',
        clientId: authStorage.clientId,
        code: code,
        redirectUri: getRootUrl()
    })

    await handleV2TokenResponse(authStorage, authV2TokenExchangeResponse);
}

export async function exchangeToken(code: string, authStorage: AuthStorage): Promise<void> {
    console.log('exchangeToken called');
    await CoreFetch<OAuthApi>(OAuthApi)
        .oauthStandardToken({
            oAuthStandardTokenRequest: {
                grantType: 'authorization_code',
                clientId: authStorage.clientId,
                codeVerifier: authStorage.codeVerifier,
                code,
            },
        })
        .then(handleTokenResponse(authStorage));
}

export async function requestAccountAccess(
    accountId: string,
    authStorage: AuthStorage,
): Promise<OAuthStandardTokenResponse> {
    console.log('requestAccountAccess called');
    clearProviderData();
    return CoreFetch<AuthApi>(AuthApi)
        .authRequestAccountAccess({
            requestAccountAccessRequest: {
                accountId,
            },
        })
        .then(handleTokenResponse(authStorage));
}

export function initCoreConfiguration(
    authStorage: AuthStorage,
    fetchController: IFetchController,
    errorContext?: IErrorContextProps,
): void {
    const isDemoMode = isDemoModeActive();

    // Demo mode no longer changes where requests GO — it stops them being made (NS-14). The
    // data comes from the generators, and the boundary below refuses anything they do not cover.
    // Previously this pointed basePath at a demo Lambda, which meant the safety of demo mode
    // depended on an env var being present on every build; if it was missing the code fell back
    // to the PRODUCTION api behind a console.warn, with the UI still reporting "Enabled".
    const basePath =
        window && window.location && window.location.host && window.location.host.indexOf('-web') > -1
            ? '/api'
            : (process.env.REACT_APP_API_URL as string);

    const middleware: Middleware[] = [];

    // FIRST, deliberately — but not for the reason an earlier version of this comment gave. It
    // claimed a blocked request is "never even credentialed"; that is false. The Authorization
    // header is set by the generated API method and reaches `request()` inside `createFetchParams`,
    // before any middleware runs, so the context this sees already carries a real token. Nothing
    // leaks — the request is discarded and never sent — but the guarantee was overstated.
    // The real reasons to be first: fail fast, and skip the abort middleware's controller
    // registration for a request that will not happen.
    if (isDemoMode) {
        middleware.push(createDemoFailClosedMiddleware());
    }

    middleware.push(
        createAuthMiddleware(authStorage, fetchController, errorContext),
        createAbortMiddleware(fetchController),
        // Observe-only: reports API responses that no longer match the generated client.
        // Never alters or rejects a response — see contract.middleware.ts.
        createContractMiddleware(),
    );

    setCoreConfiguration(
        new Configuration({
            basePath,
            middleware,
            accessToken: (): string => `Bearer ${authStorage.accessToken}`,
        }),
    );
}

export function removeHashFromUrl(): void {
    window.history.pushState('', window.document.title, window.location.pathname + window.location.search);
}

export function removeCodeFromUrl(): void {
    const params = new URLSearchParams(location.search);
    params.delete('code');
    if(params.has("state")) {
        params.delete("state");
    }
    const paramsString = params.toString().length > 0 ? '?' + params.toString() : '';
    window.history.pushState('', window.document.title, window.location.pathname + paramsString);
}

export async function logoutAndRedirect(authStorage: AuthStorage, params?: Map<string, string>): Promise<void> {
    logout(authStorage);
    const url = await buildAuthorizeUrl(authStorage, params);
    window.location.href = url;
}

export function logout(authStorage: AuthStorage): void {
    authStorage.logoutClear();
    clearProviderData();
}

function clearProviderData() {
    TableStorage.clear(new RegExp(/table_.*_defaults/g));
}

/**
 * Origin of the running app, used as the OAuth `redirect_uri`.
 *
 * This used to build the URL by hand and appended the port to `window.location.host` —
 * which already contains it. On any origin with an explicit port that produced
 * `http://localhost:3001:3001/`, and the auth server rejected it as unregistered. It went
 * unnoticed because deployed origins and ngrok tunnels both run on the default port, where
 * the condition never fired.
 *
 * `location.origin` is protocol + host and gets every case right on its own.
 */
function getRootUrl(): string {
    return window.location.origin + '/';
}

async function buildAuthorizeUrl(authStorage: AuthStorage, params?: Map<string, string>): Promise<string> {
    const { codeChallenge, submittedState } = authStorage.loginPayload();

    const v2Flow = true; //(authStorage.v2Auth !== undefined && authStorage.v2Auth === 'true');

    if(v2Flow) {
        let retVal = '';
        const jsonVal = {
            client_id: authStorage.clientId,
            redirect_uri: getRootUrl(),
            state: submittedState
        };
        Object.keys(jsonVal).forEach((v, i) => {
            if(i > 0) retVal+= "&";
            retVal+= encodeURIComponent(v) + "=" + encodeURIComponent(jsonVal[v as keyof typeof jsonVal]);
        });
        return API_BASE + '/auth/v2/authorize?' + retVal;
    } else {
        let retUrl =
            URL_BASE +
            '/authorize?&scope=&response_type=code&client_id=' +
            authStorage.clientId +
            '&code_challenge=' +
            codeChallenge +
            '&code_challenge_method=S256&state=' +
            submittedState;

        const redirectUri = _.get(params, 'redirect_uri', window.location.href.split('?')[0]);
        if (!_.isNil(redirectUri)) {
            retUrl += `&redirect_uri=${encodeURIComponent(redirectUri)}`;
        }
        const invitationCode = _.get(params, 'invitation_code');
        if (!_.isNil(invitationCode)) {
            retUrl += `&invitation_code=${encodeURIComponent(invitationCode)}`;
        }

        return retUrl;
    }

}

async function handleRefreshToken(authStorage: AuthStorage): Promise<OAuthStandardTokenResponse> {
    const refreshToken = authStorage.refreshToken;
    if (!refreshToken) {
        throw new Error('Refresh token is not exist');
    }

    return CoreFetch<OAuthApi>(OAuthApi).oauthStandardToken({
        oAuthStandardTokenRequest: {
            grantType: 'refresh_token',
            clientId: authStorage.clientId,
            refreshToken,
        },
    });
}

/**
 * Middleware checks if refresh token required and repeats requests
 */
function createAuthMiddleware(
    authStorage: AuthStorage,
    fetchController: IFetchController,
    errorContext?: IErrorContextProps,
): Middleware {
    return {
        pre: async (context: RequestContext): Promise<FetchParams | void> => {
            const authorization = _.get(context, 'init.headers.Authorization', '');
            if (authorization === 'Bearer null' || authorization === 'Bearer undefined') {
                const refreshToken = authStorage.refreshToken;
                console.warn('Logout on account of null bearer token. Current refresh token:', refreshToken);
                await logoutAndRedirect(authStorage);
            }
        },
        post: async (context: ResponseContext): Promise<Response> => {
            if (context.response.status === 401) {
                try {
                    const { accessToken, refreshToken } = await handleRefreshToken(authStorage);

                    if (accessToken && refreshToken) {
                        console.log('createAuthMiddleware setting tokens');
                        authStorage.accessToken = accessToken;
                        authStorage.refreshToken = refreshToken;

                        // Manually set the token and repeat the request
                        const response = await context.fetch(context.url, {
                            ...context.init,
                            headers: {
                                ...context.init.headers,
                                Authorization: `Bearer ${accessToken}`,
                            },
                        });
                        return response;
                    }
                } catch (err) {
                    console.log('createAuthMiddleware err=', err);
                    if (err && typeof err === 'object' && 'status' in err && typeof err.status === 'number' && err.status >= 400 && err.status < 500) {
                        await logoutAndRedirect(authStorage);
                    } else {
                        console.log('Unexpected 500 error');
                        throw err;
                    }
                }
            }

            const { opts } = fetchController;

            if (context.response.ok === false && !opts?.skipErrorHandler) {
                errorContext?.handleError(context.response.clone());
            }

            return context.response;
        },
    };
}

function createAbortMiddleware(fetchController: IFetchController): Middleware {
    return {
        pre: async (context: RequestContext): Promise<FetchParams | void> => {
            // Add the abort controller signal to Fetch params
            const { url, init } = context;
            const { group, map } = fetchController;
            const controller = new AbortController();
            if (!map.has(group)) {
                map.set(group, []);
            }
            const controllers = map.get(group);
            if (controllers) {
                controllers.push(controller);
                init.signal = controller.signal;
            }
            return { url, init };
        },
        post: async (context: ResponseContext): Promise<Response> => {
            // always deregister controller
            if (context.response.status === 200) {
                const { group, map } = fetchController;
                if (map.has(group)) {
                    const controllers = map.get(group);
                    if (controllers && context.init.signal) {
                        const controller = _.find(controllers, { signal: context.init.signal });
                        if (controller) {
                            map.set(group, _.without(controllers, controller) as AbortController[]);
                        }
                    }
                }
            }
            return context.response;
        },
    };
}

/**
 * The NS-14 boundary: in demo mode, do not make the request.
 *
 * Demo coverage is per call site and always partial, so a screen the generators do not cover
 * would otherwise fetch from production as the signed-in user and render that account's real
 * data — silently, and indistinguishably from generated data. This makes that impossible:
 * anything outside the identity allowlist fails, loudly and harmlessly, instead of succeeding.
 *
 * It replaces a middleware that used to fabricate pagination headers for the demo Lambda's
 * header-less array responses. The generators paginate correctly on their own, so that
 * workaround — and the one-page ceiling it silently imposed on any demo dataset — is gone.
 */
function createDemoFailClosedMiddleware(): Middleware {
    return {
        pre: async (context: RequestContext): Promise<FetchParams | void> => {
            const method = (context.init && (context.init.method as string)) || 'GET';
            if (isDemoRequestAllowed(context.url, method)) {
                return;
            }
            throw new DemoModeBlockedError(context.url, method);
        },
    };
}

function handleTokenResponse(authStorage: AuthStorage): (r: OAuthStandardTokenResponse) => OAuthStandardTokenResponse {
    return (response: OAuthStandardTokenResponse): OAuthStandardTokenResponse => {
        console.log('handleTokenResponse response=', response);
        const { accessToken, refreshToken } = response;
        authStorage.accessToken = accessToken;
        authStorage.refreshToken = refreshToken;
        return response;
    };
}

function handleV2TokenResponse(authStorage: AuthStorage, response: OAuthStandardTokenResponse): OAuthStandardTokenResponse  {
    console.log('handleTokenResponse response=', response);
    const { accessToken, refreshToken } = response;
    authStorage.accessToken = accessToken;
    authStorage.refreshToken = refreshToken;
    return response;
}
